<template>
  <UInput
    ref="inputRef"
    v-bind="inputAttributes"
    :model-value="localInputValue"
    :placeholder="placeholder"
    :disabled="disabled"
    type="text"
    autocomplete="off"
    :class="attrs.class"
    :ui="controlUi"
    @update:model-value="onInput"
  >
    <template #leading>
      <MagnifyingGlassIcon class="dark:text-content-level-500 size-4 text-content-level-400" />

      <span
        v-if="selectedScope"
        ref="scopeBadgeRef"
        class="bg-primary/10 border border-primary/30 dark:bg-primary/20 dark:border-primary/40 dark:text-primary font-semibold gap-1 inline-flex items-center ms-1.5 pointer-events-auto px-2 py-0.5 rounded-full text-[11px] text-primary"
      >
        /{{ selectedScope }}:
        <button
          type="button"
          :disabled="disabled"
          class="cursor-pointer dark:hover:bg-primary/25 dark:text-primary hover:bg-primary/15 inline-flex items-center justify-center rounded-full size-3.5 text-primary transition"
          :aria-label="$t('common.search.removeScope')"
          @click.stop.prevent="clearScope"
        >
          <XMarkIcon class="size-3" />
        </button>
      </span>
    </template>

    <template
      v-if="showClearButton"
      #trailing
    >
      <button
        type="button"
        class="cursor-pointer dark:hover:bg-surface-canvas/10 dark:hover:text-content-level-300 dark:text-content-level-500 hover:bg-surface-level-100 hover:text-content-level-600 inline-flex items-center justify-center rounded-full size-6 text-content-level-400 transition"
        :aria-label="$t('common.search.clearSearch')"
        @click="clearInput"
      >
        <XMarkIcon class="size-4" />
      </button>
    </template>

    <Transition
      enter-active-class="transition-all duration-180 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-120 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="showCommandPalette"
        class="absolute backdrop-blur-sm bg-surface-canvas/95 border border-primary/25 dark:bg-surface-level-900/95 dark:border-primary/35 left-0 mt-2 overflow-hidden right-0 rounded-xl shadow-lg top-full z-(--z-index-ds-lifted)"
      >
        <button
          v-for="option in filteredCommandOptions"
          :key="option.value"
          type="button"
          class="cursor-pointer flex gap-3 items-start justify-between px-3 py-2.5 text-left transition-colors w-full"
          :class="[
            highlightedOptionValue === option.value
              ? 'bg-primary/15 dark:bg-primary/20'
              : 'hover:bg-primary/5 dark:hover:bg-primary/10'
          ]"
          :data-command-value="option.value"
          @mouseenter="() => highlightOption(option.value)"
          @focus="() => highlightOption(option.value)"
          @keydown="handleOptionKeydown"
          @click="() => selectOption(option.value)"
        >
          <div class="min-w-0">
            <p class="dark:text-content-inverse font-semibold text-content-level-900 text-sm">
              /{{ option.value }}
            </p>

            <p
              v-if="option.label"
              class="dark:text-content-level-400 text-content-level-500 text-xs"
            >
              {{ option.label }}
            </p>
          </div>

          <span
            v-if="option.icon"
            class="bg-primary/10 border border-primary/30 dark:bg-primary/20 dark:border-primary/40 dark:text-primary inline-flex items-center justify-center mt-0.5 rounded-full size-7 text-primary"
          >
            <component
              :is="option.icon"
              class="size-3.5"
            />
          </span>
        </button>

        <p
          v-if="filteredCommandOptions.length === 0"
          class="dark:text-content-level-400 px-3 py-2.5 text-content-level-500 text-xs"
        >
          {{ $t('common.search.noMatchingScopeKeepTypingToFilter') }}
        </p>
      </div>
    </Transition>
  </UInput>
</template>

<script setup lang="ts">
import { computed, mergeProps, ref, toRef, unref, useAttrs, useTemplateRef, watch } from 'vue'
import UInput from '@nuxt/ui/components/Input.vue'

import type { SlashCommandOption } from '~/interfaces/SlashCommandOption'
import { useScopeBadgePadding } from '~/composables/useScopeBadgePadding'
import { useSlashCommandPalette } from '~/composables/useSlashCommandPalette'
import { fieldControlInput } from '~/design-system/base/fieldControlInput'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { XMarkIcon } from '@heroicons/vue/20/solid'

const props = withDefaults(
  defineProps<{
    /** Current search input value. */
    modelValue: string
    /** Active slash-command scope value. */
    commandModelValue?: string
    /** Enables slash-command parsing and the command palette. */
    enableSlashCommands?: boolean
    /** Slash-command options displayed in the command palette. */
    slashCommandOptions?: readonly SlashCommandOption[]
    /** Placeholder text shown when the input is empty. */
    placeholder?: string
    /** Optional id attribute for the input element. */
    id?: string | undefined
    /** Optional name attribute for the input element. */
    name?: string | undefined
    /** Disables editing and clear interactions. */
    disabled?: boolean
    /** Additional classes applied to the input element. */
    inputClass?: string
  }>(),
  {
    commandModelValue: '',
    id: undefined,
    inputClass: '',
    name: undefined,
    placeholder: 'Search',
    slashCommandOptions: () => []
  }
)

const emit = defineEmits<{
  (_event: 'update:modelValue' | 'update:commandModelValue', _value: string): void
  (_event: 'clear'): void
}>()

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const modelValue = toRef(props, 'modelValue')
const localInputValue = ref(unref(modelValue))

const applyScope = (value: string, term = '') => {
  emit('update:commandModelValue', value)
  emit('update:modelValue', term)
  localInputValue.value = term
}

const inputRef = useTemplateRef<{ inputRef: HTMLInputElement | null }>('inputRef')

const {
  dismissPrompt,
  filteredCommandOptions,
  handleKeydown: handlePaletteKeydown,
  handleOptionKeydown,
  highlightOption,
  highlightedOptionValue,
  isInteractionAllowed,
  isPaletteOpen: showCommandPalette,
  matchTypedCommand,
  resetPalette,
  selectOption
} = useSlashCommandPalette({
  inputRef,
  isInteractive: () => !props.disabled,
  onSelect: applyScope,
  options: () => props.slashCommandOptions
})

const scopeBadgeRef = useTemplateRef<HTMLElement>('scopeBadgeRef')

watch(
  () => props.modelValue,
  (value) => {
    if (value !== localInputValue.value) {
      localInputValue.value = value
    }
  }
)

const selectedScope = computed(() => {
  const scope = props.commandModelValue.trim()
  if (!props.enableSlashCommands || !scope) return ''
  return scope
})

const { badgePaddingStyle } = useScopeBadgePadding(scopeBadgeRef, () => selectedScope.value)

const showClearButton = computed(() => localInputValue.value.trim().length > 0 && !props.disabled)

// Shares the field skin with the field molecules and the Select atom.
const controlUi = computed(() => ({
  // ps-9/pe-9 come after the skin's px-3 in the merged class string, so they win.
  ...fieldControlInput.ui(false, `ps-9 ${showClearButton.value ? 'pe-9' : ''} ${props.inputClass}`),
  // The palette positions against the root; Nuxt UI's own layout has no reason to.
  root: 'relative w-full'
}))

const inputAttributes = computed(() => {
  const { class: _class, style, ...rest } = attrs
  return mergeProps(fieldControlInput.props, rest, {
    onKeydown,
    style: [style, badgePaddingStyle.value],
    ...(props.id === undefined ? {} : { id: props.id }),
    ...(props.name === undefined ? {} : { name: props.name })
  })
})

const onKeydown = (event: KeyboardEvent) => {
  handlePaletteKeydown(event)
  // Backspace in an empty input removes the active scope.
  if (event.key !== 'Backspace' || !selectedScope.value) return
  if (localInputValue.value.trim().length > 0) return

  event.preventDefault()
  clearScope()
}

const onInput = (value: string | number) => {
  const rawValue = String(value)
  localInputValue.value = rawValue

  if (props.enableSlashCommands && rawValue.startsWith('/')) {
    const match = matchTypedCommand(rawValue)
    if (match) applyScope(match.scope, match.term)
    else emit('update:modelValue', '')
    return
  }

  dismissPrompt()
  emit('update:modelValue', rawValue)
}

const clearInput = () => {
  resetPalette()
  localInputValue.value = ''
  emit('update:modelValue', '')
  emit('clear')
}

const clearScope = () => {
  if (!isInteractionAllowed.value) return
  emit('update:commandModelValue', '')
}
</script>
